import React from "react";
import clsx from "clsx";
import { useHistory } from "react-router-dom";
import { IonIcon, IonItemGroup, useIonViewWillEnter } from "@ionic/react";

import { convertDateToDateString } from "../../../libs/Util";
import Dropdown from "../../../atoms/Dropdown";
import BoxAnalysisItem from "../../../atoms/BoxAnalysisItem";
import DatePicker from "../../../atoms/DatePicker";
import SearchField from "../../../atoms/SearchField";
import mailIcon from "../../../assets/icons/mail.svg";
import { BOX_SORT_DATA, EOrderBy } from "../../../constants/boxDashboard";
import {
  IClass,
  ITeacher,
  IBoxDashboard,
  IMyBox,
  TeacherGearboxDirectory,
} from "../../../state";
import {
  manageBoxesPath,
  realizationsTeacherPath,
  realizationsBoxPath,
} from "../../../store/teacher";
import { IBoxesDashboardParams } from "../../../constants/boxDashboard";
import BoxDirectoryList from "../../../organisms/teacher/BoxDirectoryList";
import { ReactComponent as GoodIcon } from "../../../assets/icons/box_good.svg";
import { ReactComponent as CommentIcon } from "../../../assets/icons/box_comment.svg";

import styles from "./BoxDashboard.module.scss";

export interface BoxDashboardProps {
  calling: boolean;
  classes: IClass[];
  teachers: ITeacher[];
  boxList: IBoxDashboard[];
  myBox: IMyBox | null;
  teacherId: number;
  assignClassIds: number[];
  teacherGearboxDirectories: TeacherGearboxDirectory[];
  fetchBoxDashboard: (params: IBoxesDashboardParams) => void;
  bulkCheckRealizations: (gear_box_id: number) => void;
  gearboxHiddenFunc: (id: number) => void;
  getRealizationsCSVDataFunc: (id: number) => void;
}

const INITIAL_SEARCH = {
  searchText: "",
  selectedClassId: 0,
  selectedTeacherId: 0,
  startDate: null,
  endDate: null,
};

const BoxDashboard: React.FC<BoxDashboardProps> = ({
  calling,
  classes,
  teachers,
  boxList,
  myBox,
  teacherId,
  assignClassIds,
  teacherGearboxDirectories,
  fetchBoxDashboard,
  bulkCheckRealizations,
  gearboxHiddenFunc,
  getRealizationsCSVDataFunc,
}) => {
  const history = useHistory();

  const [state, setState] = React.useState<{
    searchText: string;
    selectedClassId: number;
    selectedTeacherId: number;
    startDate: Date | null;
    endDate: Date | null;
    orderBy: EOrderBy;
    selectedDirectory: string;
    selectedDirectoryId: number;
    boxList: IBoxDashboard[];
  }>({
    ...INITIAL_SEARCH,
    orderBy: EOrderBy.LATEST_REALIZATION_CREATED_AT,
    selectedDirectory: "全て",
    selectedDirectoryId: 0,
    boxList: [],
  });

  const classesData = React.useMemo(
    () =>
      classes.map(cls => ({
        value: cls.id,
        label: `${cls.full_name} (${cls.year})`,
      })),
    [classes],
  );

  const onInputChange = React.useCallback(
    e => setState(s => ({ ...s, searchText: e.target.value })),
    [],
  );

  const onDropdownSelect = React.useCallback(
    ({ name, value }) =>
      setState(s => ({
        ...s,
        [name]: value,
      })),
    [],
  );

  const onDatePickerChange = React.useCallback(([start, end]) => {
    setState(s => ({
      ...s,
      startDate: start,
      endDate: end,
    }));
  }, []);

  const isDatePickerInvalid = React.useMemo(
    () =>
      (!!state.startDate && !state.endDate) ||
      (!state.startDate && !!state.endDate),
    [state.startDate, state.endDate],
  );

  const getBoxDashboard = React.useCallback(() => {
    fetchBoxDashboard({
      title: state.searchText,
      class_id: state.selectedClassId,
      start_date: state.startDate,
      end_date: state.endDate,
    });
  }, [
    fetchBoxDashboard,
    state.searchText,
    state.selectedClassId,
    state.startDate,
    state.endDate,
  ]);

  useIonViewWillEnter(() => {
    getBoxDashboard();
  });

  React.useEffect(() => {
    if (!isDatePickerInvalid || (!state.startDate && !state.endDate))
      getBoxDashboard();
  }, [state.startDate, state.endDate, isDatePickerInvalid, getBoxDashboard]);

  const sortCoreBoxList = React.useCallback((list: IBoxDashboard[]) => {
    return [...list].sort((a, b) => (b.order_num || 0) - (a.order_num || 0));
  }, []);

  const sortBoxList = React.useCallback(
    (orderBy: EOrderBy, list: IBoxDashboard[]) => {
      const sortBy = {
        // 提出日が新しい順でソート
        [EOrderBy.LATEST_REALIZATION_CREATED_AT]: (
          a: IBoxDashboard,
          b: IBoxDashboard,
        ) =>
          new Date(b.latest_realization_created_at ?? 0).getTime() -
          new Date(a.latest_realization_created_at ?? 0).getTime(),
        // ストック数でソート
        [EOrderBy.REALIZATION_COUNT]: (a: IBoxDashboard, b: IBoxDashboard) =>
          b.realizations_count - a.realizations_count,
        // いいね数でソート
        [EOrderBy.FAVORITE_COUNT]: (a: IBoxDashboard, b: IBoxDashboard) =>
          (b.favorites_count ?? 0) - (a.favorites_count ?? 0),
        // コメント数でソート
        [EOrderBy.COMMENTS_COUNT]: (a: IBoxDashboard, b: IBoxDashboard) =>
          (b.comments_count ?? 0) - (a.comments_count ?? 0),
      } as Record<EOrderBy, (a: IBoxDashboard, b: IBoxDashboard) => number>;

      return [...list].sort(sortBy[orderBy] || (() => 0));
    },
    [],
  );

  const sortFilteredBoxList = React.useCallback(
    (orderBy: EOrderBy, list: IBoxDashboard[]) => {
      const isCoreDirectory = teacherGearboxDirectories.some(
        d => d.klass_id && d.id === state.selectedDirectoryId,
      );

      return isCoreDirectory
        ? sortCoreBoxList(list)
        : sortBoxList(orderBy, list);
    },
    [
      state.selectedDirectoryId,
      teacherGearboxDirectories,
      sortCoreBoxList,
      sortBoxList,
    ],
  );

  const visibleBoxes = React.useMemo(
    () => boxList.filter(box => !box.is_hidden),
    [boxList],
  );

  const isSharedMyClassesBox = React.useCallback(
    (box: IBoxDashboard) =>
      (box.is_shared &&
        !!box.klasses?.some(klass => assignClassIds.includes(klass.id))) ||
      (!box.is_shared &&
        box.teacher.id !== teacherId &&
        !!box.assigned_teachers?.some(
          assignedTeacher => assignedTeacher.id === teacherId,
        )),
    [assignClassIds, teacherId],
  );

  const getFilteredList = React.useCallback(
    (directoryId?: number, selectedTeacherId?: number) => {
      // 現在の先生と異なる場合、選択された先生のBOXを表示
      if (selectedTeacherId && selectedTeacherId !== teacherId) {
        return visibleBoxes.filter(
          box =>
            box.teacher.id === selectedTeacherId && !isSharedMyClassesBox(box),
        );
      }
      // 全てのディレクトリが選択された場合
      if ((directoryId || 0) === 0) {
        return visibleBoxes.filter(
          box => box.teacher.id === teacherId || isSharedMyClassesBox(box),
        );
      }

      // COREの授業の振り返りのディレクトリが選択された場合
      const coreDirectory = teacherGearboxDirectories.find(
        d => d.klass_id && d.id === directoryId,
      );

      if (coreDirectory) {
        return visibleBoxes.filter(
          box =>
            box.directory_ids.includes(coreDirectory.id) &&
            box.teacher?.id === null,
        );
      }

      // 通常のディレクトリが選択された場合
      return visibleBoxes.filter(
        box =>
          !!directoryId &&
          box.directory_ids.includes(directoryId) &&
          (box.teacher.id === teacherId || isSharedMyClassesBox(box)),
      );
    },
    [isSharedMyClassesBox, visibleBoxes, teacherId, teacherGearboxDirectories],
  );

  React.useEffect(() => {
    if (teachers.length === 0) return;
    const teacherBoxList =
      state.selectedTeacherId || state.selectedDirectoryId !== 0
        ? getFilteredList(state.selectedDirectoryId, state.selectedTeacherId)
        : boxList.filter(
            box => box.teacher.id === teacherId || isSharedMyClassesBox(box),
          );
    setState(prevState => ({
      ...prevState,
      boxList: sortFilteredBoxList(state.orderBy, teacherBoxList),
    }));
  }, [
    boxList,
    getFilteredList,
    sortFilteredBoxList,
    state.orderBy,
    state.selectedDirectory,
    state.selectedDirectoryId,
    state.selectedTeacherId,
    teacherId,
    teachers.length,
    isSharedMyClassesBox,
  ]);

  const handleDirectoryChange = React.useCallback(
    ({
      directoryName,
      directoryId,
      teacherId: selectedTeacherId,
    }: {
      directoryName: string;
      directoryId?: number;
      teacherId?: number;
    }) => {
      const filteredList = getFilteredList(directoryId, selectedTeacherId);
      setState(s => {
        const newOrderBy =
          s.selectedDirectory === directoryName &&
          s.selectedDirectoryId === (directoryId || 0) &&
          s.selectedTeacherId === (selectedTeacherId || 0)
            ? s.orderBy
            : EOrderBy.LATEST_REALIZATION_CREATED_AT;

        return {
          ...s,
          selectedDirectory: directoryName,
          selectedDirectoryId: directoryId || 0,
          boxList: sortFilteredBoxList(newOrderBy, filteredList),
          orderBy: newOrderBy,
          selectedTeacherId: selectedTeacherId ? selectedTeacherId : 0,
        };
      });
    },
    [getFilteredList, sortFilteredBoxList],
  );

  const handleOrderByChange = (orderBy: EOrderBy) => {
    setState(s => ({
      ...s,
      orderBy,
      boxList: sortFilteredBoxList(orderBy, s.boxList),
    }));
  };

  return (
    <div className={styles.wrapper}>
      <BoxDirectoryList
        boxList={boxList}
        onDirectorySelect={handleDirectoryChange}
        teacherId={teacherId}
        isSharedMyClassesBox={isSharedMyClassesBox}
        teacherGearboxDirectories={teacherGearboxDirectories}
      />
      <div className={styles.mainContent}>
        <div className={styles.actionButtons}>
          <div className={styles.selectedDirectory}>
            {state.selectedDirectory}
          </div>
          <div className={styles.boxInfo}>
            {myBox && (
              <div className={styles.read}>
                <IonIcon
                  className={styles.readIcon}
                  icon={mailIcon}
                  onClick={() => {
                    history.push(realizationsTeacherPath);
                    bulkCheckRealizations(0);
                  }}
                />
                {myBox.has_unread_realization && (
                  <span className={styles.badge} />
                )}
              </div>
            )}
            <div
              className={styles.btnAddBox}
              onClick={() => history.push(manageBoxesPath)}
            >
              BOXを編集
            </div>
          </div>
        </div>
        <div className={styles.searchBox}>
          <div className={styles.searchOptions}>
            <SearchField
              className={styles.searchField}
              searchText={state.searchText}
              onChange={onInputChange}
            />
            <Dropdown
              variant={"secondary"}
              className={styles.dropdown}
              classNameLabelArea={styles.labelArea}
              name="selectedClassId"
              data={classesData}
              defaultLabel="クラス/グループ"
              defaultValue={0}
              selectedValue={state.selectedClassId}
              onSelect={onDropdownSelect}
            />
            <DatePicker
              variant={"secondary"}
              className={styles.datePicker}
              classNameDatePickerInput={styles.datePickerInput}
              error={isDatePickerInvalid ? "開始日と終了日が必要です" : ""}
              label={
                state.startDate || state.endDate
                  ? `${
                      state.startDate
                        ? convertDateToDateString(state.startDate)
                        : ""
                    }→${
                      state.endDate
                        ? convertDateToDateString(state.endDate)
                        : ""
                    }`
                  : "BOXへの提出日"
              }
              dateFormat="yyyy/MM/dd"
              locale="ja"
              selected={state.startDate}
              onChange={onDatePickerChange}
              startDate={state.startDate}
              endDate={state.endDate}
              selectsRange
            />
          </div>
        </div>
        {!teacherGearboxDirectories.some(
          d => d.klass_id && d.id === state.selectedDirectoryId,
        ) && (
          <div className={styles.sortTabs}>
            {BOX_SORT_DATA.map(tab => (
              <div
                key={tab.value}
                className={clsx(styles.tab, {
                  [styles.selected]: tab.value === state.orderBy,
                })}
                onClick={() => handleOrderByChange(tab.value as EOrderBy)}
              >
                {tab.label}
              </div>
            ))}
          </div>
        )}
        <div className={styles.boxLabels}>
          <div className={clsx(styles.label, styles.boxName)}>BOX名</div>
          <div className={clsx(styles.label, styles.stock)}>ストック</div>
          <div className={clsx(styles.label, styles.good)}>
            <GoodIcon className={styles.icon} />
            いいね
          </div>
          <div className={clsx(styles.label, styles.comment)}>
            <CommentIcon className={styles.icon} />
            コメント
          </div>
          <div className={clsx(styles.label, styles.status)}>ステータス</div>
          <div className={clsx(styles.label, styles.teacherName)}>作成者</div>
        </div>
        <IonItemGroup className={styles.boxList}>
          {state.boxList.length === 0 ? (
            <div className={styles.emptyData}>該当するデータがありません。</div>
          ) : (
            state.boxList
              .filter(box => !box.is_hidden)
              .map(box => (
                <BoxAnalysisItem
                  key={box.id}
                  boxData={box}
                  isSharedBox={isSharedMyClassesBox(box)}
                  onClick={() => {
                    history.push(`${realizationsBoxPath}/${box.id}`);
                    bulkCheckRealizations(box.id);
                  }}
                  gearboxHiddenFunc={gearboxHiddenFunc}
                  getRealizations4CSV={getRealizationsCSVDataFunc}
                  teacherId={teacherId}
                  calling={calling}
                />
              ))
          )}
        </IonItemGroup>
      </div>
    </div>
  );
};

export default BoxDashboard;
